<script setup>
import { computed, ref, watch } from "vue";
import { ArgonLoadingButton } from "@/components";
import { useUserStore } from "@/_store/userStore.js";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonPhoneInput from "@/components/ArgonPhoneInput.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import ModalBase from "@/components/modal/ModalBase.vue";
import { errorUtils } from "@/utils";

const userStore = useUserStore();

const createEmptyData = () => ({
  id: "",
  user: "",
  name: "",
  phone: "",
});

const data = ref(createEmptyData());

const rules = computed(() => ({
  phone: {
    required: helpers.withMessage("Teléfono es requerido", required),
    valid: helpers.withMessage(
      "Teléfono no es válido",
      (value) => !value || /^\+\d{7,18}$/.test(String(value).trim())
    ),
  },
}));
const v$ = ref(useVuelidate(rules, data));

const snackbar = ref(null);
const closeSnackbar = () => {
  snackbar.value = null;
};

function setUser(userItem) {
  data.value = {
    id: userItem?._id || "",
    user: userItem?.user || "",
    name: userItem?.name || "",
    phone: userItem?.phone || "",
  };
  v$.value.$reset();
}

async function submit() {
  await v$.value.$validate();
  const message = v$.value.$errors
    .map((error) => `<li>${error.$message}</li>`)
    .join("");
  if (message) {
    snackbar.value = {
      title: "Error",
      message: `<ul>${message}</ul>`,
      type: "danger",
      color: "white",
    };
    return;
  }

  userStore.modifyUser(data.value.id, {
    name: String(data.value.name || "").trim(),
    phone: String(data.value.phone || "").trim(),
  });
}

function cleanData() {
  data.value = createEmptyData();
  v$.value.$reset();
}

watch(
  () => userStore.error,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(value),
        type: "danger",
        color: "white",
      };
    }
  }
);

const disableButton = computed({
  get() {
    return userStore.disableButton;
  },
});

defineExpose({ setUser });
</script>

<template>
  <modal-base ref="editUserModal" @hide="cleanData">
    <template #header>
      <h6 id="ModalLabel" class="modal-title">Modificar</h6>
    </template>
    <template #body>
      <form class="border-0">
        <div>
          <div class="row">
            <div class="col-2">
              <label class="col-form-label">Email</label>
            </div>
            <div class="col-10 d-flex align-items-center">
              <span class="modal-display-value">{{ data.user }}</span>
            </div>
          </div>
        </div>
        <div>
          <div class="row">
            <div class="col-2">
              <label class="col-form-label">Nombre</label>
            </div>
            <div class="col-10">
              <argon-input
                id="edit-user-name"
                v-model="data.name"
                :max-length="75"
                type="text"
                name="name"
                size="lg"
              />
            </div>
          </div>
        </div>
        <div>
          <div class="row">
            <div class="col-2">
              <label class="col-form-label required">Teléfono</label>
            </div>
            <div class="col-10">
              <argon-phone-input
                id="edit-user-phone"
                v-model="data.phone"
                :error="v$.phone.$error"
              />
            </div>
          </div>
        </div>
      </form>
    </template>
    <template #footer>
      <argon-loading-button
        class="btn btn-xs bg-gradient-primary"
        color="primary"
        variant="gradient"
        :label="disableButton ? 'Guardando...' : 'Guardar'"
        :disable-button="disableButton || !data.id"
        @click="submit"
      >
      </argon-loading-button>
    </template>
    <div class="position-fixed top-1 end-1">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
<style lang="scss"></style>
